import { computed, onScopeDispose, type Ref, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useCartStore } from '@/stores/cart'

import { leaveCompletedOrder } from '@/composables/completedOrder'
import { browserPaymentInProgress, tabHoldsPayment } from '@/lib/paymentHoldMarker'

/** How often a showing modal re-checks the hold, so a hold ended on another device is noticed. */
export const PAYMENT_HOLD_RECHECK_INTERVAL_MS = 15_000

/** How long a paid cart waits for the tab in this browser that paid for it to say it has finished. */
export const PAYING_TAB_GRACE_MS = 60_000

export interface PaymentHoldModal {
  isShown: Readonly<Ref<boolean>>
  isChecking: Readonly<Ref<boolean>>
  checkAgain: () => Promise<void>
}

/**
 * Whether `PaymentHoldModal` shows: the cart is held for a payment another tab or device started,
 * and the route isn't one that has to stay usable (`meta.skipPaymentHoldModal`). While it shows and
 * the page is visible, the hold is re-checked on a timer.
 *
 * A cart that turns out paid leaves for the subject's landing. When a tab in this browser paid for
 * it, the modal stays up until that tab says it has finished (`App.vue` takes the message): that
 * tab is still on its way back from the payment provider and needs the saved cart to record the
 * purchase. Only past `PAYING_TAB_GRACE_MS`, or when the payment was on another device, does this
 * tab clear the cart itself.
 */
export function usePaymentHoldModal(): PaymentHoldModal {
  const cartStore = useCartStore()
  const route = useRoute()
  const router = useRouter()

  // `tabHoldsPayment` reads `sessionStorage`, which Vue can't track. The marker only changes in the
  // paying tab, alongside its cart, so this recomputes when the answer can change.
  const isHeldElsewhere = computed(
    () =>
      cartStore.isHeldForPayment &&
      !cartStore.cartData?.isLocked &&
      !tabHoldsPayment(cartStore.cartData?.order?.orderId) &&
      !route.meta.skipPaymentHoldModal
  )

  const isAwaitingPayingTab = ref(false)
  const isShown = computed(() => isHeldElsewhere.value || isAwaitingPayingTab.value)

  const isChecking = ref(false)

  async function checkAgain(): Promise<void> {
    if (isChecking.value) return

    isChecking.value = true
    try {
      await cartStore.recheckPaymentHold()
    } finally {
      isChecking.value = false
    }
  }

  let timer: ReturnType<typeof setInterval> | null = null
  let graceTimer: ReturnType<typeof setTimeout> | null = null

  function syncPolling(): void {
    const shouldPoll = isHeldElsewhere.value && document.visibilityState === 'visible'

    if (shouldPoll && !timer) {
      timer = setInterval(
        () => void cartStore.recheckPaymentHold(),
        PAYMENT_HOLD_RECHECK_INTERVAL_MS
      )
    } else if (!shouldPoll && timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function leavePaidCart(): void {
    if (!browserPaymentInProgress(cartStore.cartData?.order?.orderId)) {
      leaveCompletedOrder(router, { clear: true })
      return
    }

    isAwaitingPayingTab.value = true
    graceTimer = setTimeout(() => leaveCompletedOrder(router, { clear: true }), PAYING_TAB_GRACE_MS)
  }

  watch(
    isHeldElsewhere,
    (held, wasHeld) => {
      syncPolling()

      if (!held && wasHeld && cartStore.cartData?.isLocked) leavePaidCart()
    },
    { immediate: true }
  )

  document.addEventListener('visibilitychange', syncPolling)

  onScopeDispose(() => {
    document.removeEventListener('visibilitychange', syncPolling)
    if (timer) clearInterval(timer)
    if (graceTimer) clearTimeout(graceTimer)
  })

  return { isShown, isChecking, checkAgain }
}
